import { Controller } from '@hotwired/stimulus'
import 'utils/multiselect' // side-effect: registers prod's global .multiselect driver (open/close/search/select/label)

// skill_list filter behaviour (ported from SkillList.mdx ## JS).
// Three pieces of state read from the DOM drive everything: the active category,
// the selected source set (multi), and the search query. Sort is always
// popularity desc (no sort UI). The controller toggles data-state on the root
// and per-card `hidden`; visibility of the fallback rows / show-all button is
// CSS-driven off data-state.
//
// The category + source dropdowns are prod's real `.multiselect` component.
// Its self-contained driver (utils/multiselect) owns open/close/search/select
// and the toggle label; this controller just listens for checkbox `change` on
// the section root (delegated) and reads the checked values back into state.
// Attached to .skill-list via data-controller="skill-list-filter".
export default class extends Controller {
  static targets = ['grid', 'input', 'noResult']
  static values = { featuredCount: { type: Number, default: 8 } }

  connect() {
    this.cards = Array.from(this.gridTarget.querySelectorAll('.skill-card'))
    this.tabs = Array.from(
      this.element.querySelectorAll('.skill-list__category')
    )
    this.categoryDropdown = this.element.querySelector(
      '.skill-list__category-dropdown'
    )
    this.sourceDropdown = this.element.querySelector(
      '.skill-list__source-dropdown'
    )
    this.category = 'All'
    this.sources = [] // empty = no source filter
    this.query = ''
    this.showAllFlag = false
    this.timer = null

    // Cache the sortable values once.
    this.cards.forEach(card => {
      card._sources = (card.dataset.sources || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
      card._count = Number(card.dataset.count) || 0
    })

    // The prod multiselect driver toggles the checkboxes; we react to their change.
    this.onMultiselectChange = e => {
      if (
        e.target.classList &&
        e.target.classList.contains('multiselect__checkbox')
      ) {
        this.syncFromMultiselects()
      }
    }
    this.element.addEventListener('change', this.onMultiselectChange)
    // "Clear All" toggles checkboxes without a change event → also resync on click.
    this.onMultiselectClear = e => {
      if (e.target.closest('.multiselect__clear'))
        requestAnimationFrame(() => this.syncFromMultiselects())
    }
    this.element.addEventListener('click', this.onMultiselectClear)

    this.render()
  }

  disconnect() {
    clearTimeout(this.timer)
    this.element.removeEventListener('change', this.onMultiselectChange)
    this.element.removeEventListener('click', this.onMultiselectClear)
  }

  // ---- multiselect → state ----

  checkedValues(dropdown) {
    if (!dropdown) return []
    return Array.from(
      dropdown.querySelectorAll('.multiselect__checkbox:checked')
    ).map(c => c.value)
  }

  syncFromMultiselects() {
    // Category single-select: none checked = All.
    const cat = this.checkedValues(this.categoryDropdown)[0] || 'All'
    if (cat !== this.category) {
      this.tabs.forEach(t =>
        t.setAttribute('aria-pressed', String(t.dataset.category === cat))
      )
    }
    this.category = cat
    // Source multi-select: none checked = no filter.
    this.sources = this.checkedValues(this.sourceDropdown)
    this.showAllFlag = false
    this.render()
  }

  // ---- filtering / rendering ----

  inCategoryOf(card, cat) {
    return cat === 'All' || (card.dataset.category || '') === cat
  }

  inSources(card) {
    if (!this.sources.length) return true
    return card._sources.some(s => this.sources.includes(s))
  }

  inQuery(card) {
    return (
      !this.query ||
      (card.dataset.name || '').toLowerCase().includes(this.query)
    )
  }

  sortCards(arr) {
    return arr.slice().sort((a, b) => b._count - a._count)
  }

  render() {
    const featuredCount = this.featuredCountValue

    const matched = this.sortCards(
      this.cards.filter(
        c =>
          this.inCategoryOf(c, this.category) &&
          this.inSources(c) &&
          this.inQuery(c)
      )
    )
    const altCategoryMatches =
      this.query && matched.length === 0 && this.category !== 'All'
        ? this.sortCards(
            this.cards.filter(
              c =>
                !this.inCategoryOf(c, this.category) &&
                this.inSources(c) &&
                this.inQuery(c)
            )
          )
        : []

    let state
    let visible
    if (this.query && matched.length === 0 && altCategoryMatches.length === 0) {
      state = 'search-no-alt'
      visible = []
    } else if (this.query && matched.length === 0) {
      state = 'search-has-alt'
      visible = altCategoryMatches
    } else if (this.showAllFlag) {
      state = 'scroll'
      visible = matched
    } else if (this.category !== 'All' || this.sources.length) {
      state = 'filter'
      visible = matched
    } else {
      state = 'default'
      visible = matched.slice(0, featuredCount)
    }

    // Toggle each card's visibility AND its DOM order to match the sorted set.
    const visibleSet = new Set(visible)
    this.cards.forEach(card => {
      card.hidden = !visibleSet.has(card)
    })
    visible.forEach(card => this.gridTarget.appendChild(card))

    this.element.dataset.state = state
    if (state.startsWith('search')) {
      const cat =
        this.category === 'All' ? '' : ` in the ${this.category} category`
      this.noResultTarget.textContent = `No result for "${this.inputTarget.value}"${cat}`
    }
  }

  // ---- category (left rail tabs + tablet/mobile dropdown) ----

  setCategory(next) {
    this.category = next
    this.tabs.forEach(t =>
      t.setAttribute('aria-pressed', String(t.dataset.category === next))
    )
    // Keep the category multiselect checkboxes in sync with the left-rail tabs.
    if (this.categoryDropdown) {
      this.categoryDropdown
        .querySelectorAll('.multiselect__checkbox')
        .forEach(c => {
          c.checked = next !== 'All' && c.value === next
        })
      const toggle = this.categoryDropdown.querySelector('.multiselect__toggle')
      if (toggle)
        toggle.textContent =
          next === 'All' ? `All ${toggle.dataset.text}` : next
    }
    this.inputTarget.value = ''
    this.query = ''
    this.showAllFlag = false
    this.render()
  }

  selectCategory(event) {
    this.setCategory(event.currentTarget.dataset.category)
  }

  // ---- search ----

  input() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => {
      this.query = this.inputTarget.value.trim().toLowerCase()
      this.render()
    }, 150)
  }

  submit(event) {
    event.preventDefault()
    clearTimeout(this.timer)
    this.query = this.inputTarget.value.trim().toLowerCase()
    this.render()
  }

  // ---- show-all ----

  showAll() {
    this.showAllFlag = true
    this.render()
  }
}
